import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { AccountantReportView } from 'shared-models/browser';
import { accountantReportService } from '../../../../services/accountant-report/accountant-report.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';
import type { AccountantReportMode } from './accountant-report.types';
import { buildAccountantReportPrintHtml, previousMonthRange } from './accountant-report.utils';
import { useAccountantReportDelivery } from './useAccountantReportDelivery';

type UseAccountantReportControllerParams = {
  opened: boolean;
  tenantId: string;
  employeeSession: string;
};

// Summary: accountant-report controller — mode/range/session state, generate, xlsx blob, print.
export function useAccountantReportController({ opened, tenantId, employeeSession }: UseAccountantReportControllerParams) {
  const delivery = useAccountantReportDelivery({ opened, tenantId, employeeSession });
  const [mode, setMode] = useState<AccountantReportMode>('range');
  const [range, setRange] = useState(() => previousMonthRange());
  const [sessionIds, setSessionIds] = useState<number[]>([]);
  const [view, setView] = useState<AccountantReportView | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (opened) {
      setMode('range');
      setRange(previousMonthRange());
      setSessionIds([]);
      setView(null);
    }
  }, [opened]);

  const showError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
    }
  }, []);

  const generate = useCallback(async () => {
    if (!employeeSession || inFlightRef.current) {
      return;
    }
    if (mode === 'sessions' && sessionIds.length === 0) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'בחר משמרת אחת לפחות', autoClose: 3000 });
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      const result =
        mode === 'range'
          ? await accountantReportService.getByRange(tenantId, range.from, range.to, employeeSession)
          : await accountantReportService.getBySessions(tenantId, sessionIds, employeeSession);
      setView(result);
    } catch (error: unknown) {
      showError(error);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [employeeSession, mode, sessionIds, tenantId, range.from, range.to, showError]);

  const exportXlsx = useCallback(async () => {
    if (!employeeSession || exporting || (mode === 'sessions' && sessionIds.length === 0)) {
      return;
    }
    setExporting(true);
    try {
      const blob =
        mode === 'range'
          ? await accountantReportService.exportRangeXlsx(tenantId, range.from, range.to, employeeSession)
          : await accountantReportService.exportSessionsXlsx(tenantId, sessionIds, employeeSession);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'דו״ח רואה חשבון.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error: unknown) {
      showError(error);
    } finally {
      setExporting(false);
    }
  }, [employeeSession, exporting, mode, sessionIds, tenantId, range.from, range.to, showError]);

  const print = useCallback(() => {
    if (!view) {
      return;
    }

    const html = buildAccountantReportPrintHtml(view);

    // Render into a detached same-origin iframe so the print output is fully
    // self-contained (framework-agnostic HTML/CSS) and never clipped by the
    // modal's layout/overflow. Iframes also avoid popup blockers.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const runPrint = () => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        cleanup();
        return;
      }
      try {
        frameWindow.focus();
        frameWindow.print();
      } finally {
        setTimeout(cleanup, 500);
      }
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      return;
    }

    iframe.addEventListener('load', runPrint, { once: true });
    doc.open();
    doc.write(html);
    doc.close();
  }, [view]);

  return {
    ...delivery,
    mode,
    setMode,
    range,
    setRange,
    sessionIds,
    setSessionIds,
    view,
    loading,
    exporting,
    generate,
    exportXlsx,
    print,
  };
}
